// src/components/healthcare/schemaBuilder/SchemaPreviewModal.tsx

import React, { useState } from "react";
import { Modal, Button, Typography, Tabs } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { TabPane } = Tabs;

interface SchemaPreviewModalProps {
	visible: boolean;
	schema: any;
	onClose: () => void;
}

const SchemaPreviewModal: React.FC<SchemaPreviewModalProps> = ({ visible, schema, onClose }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// Generate example data structure
	const generateExample = (schema: any): any => {
		if (!schema || !schema.properties) return {};

		const example: any = {};
		for (const [key, prop] of Object.entries(schema.properties)) {
			const propSchema = prop as any;

			if (propSchema.type === "object" && propSchema.properties) {
				example[key] = generateExample(propSchema);
			} else if (propSchema.type === "array") {
				if (propSchema.items?.type === "object") {
					example[key] = [generateExample(propSchema.items)];
				} else {
					example[key] = [propSchema.items?.type === "number" ? 0 : "example"];
				}
			} else if (propSchema.type === "number") {
				example[key] = 0;
			} else if (propSchema.type === "boolean") {
				example[key] = false;
			} else if (propSchema.enum) {
				example[key] = propSchema.enum[0];
			} else {
				example[key] = "example";
			}
		}
		return example;
	};

	const exampleData = generateExample(schema);

	return (
		<Modal
			title="Schema Preview"
			open={visible}
			onCancel={onClose}
			width={760}
			footer={[
				<Button key="copy" icon={copied ? <CheckOutlined /> : <CopyOutlined />} onClick={handleCopy}>
					{copied ? "Copied" : "Copy JSON"}
				</Button>,
				<Button key="close" type="primary" onClick={onClose}>
					Close
				</Button>,
			]}
		>
			<Tabs defaultActiveKey="schema" size="small">
				<TabPane tab="JSON Schema" key="schema">
					<div className="bg-gray-50 rounded overflow-auto" style={{ maxHeight: 460 }}>
						<pre className="text-[11px] font-mono p-4 m-0">{JSON.stringify(schema, null, 2)}</pre>
					</div>
				</TabPane>

				<TabPane tab="Example Output" key="example">
					<Text type="secondary" className="text-xs block mb-2">
						Example of what the AI will return based on this schema:
					</Text>
					<div className="bg-gray-50 rounded overflow-auto" style={{ maxHeight: 440 }}>
						<pre className="text-[11px] font-mono p-4 m-0">{JSON.stringify(exampleData, null, 2)}</pre>
					</div>
				</TabPane>

				<TabPane tab="Summary" key="info">
					<div className="space-y-3">
						<div className="flex gap-6">
							<div>
								<Text type="secondary" className="text-[10px] uppercase tracking-wide block mb-0.5">Total Fields</Text>
								<Text strong className="text-sm">{Object.keys(schema?.properties || {}).length}</Text>
							</div>
							<div>
								<Text type="secondary" className="text-[10px] uppercase tracking-wide block mb-0.5">Required</Text>
								<Text strong className="text-sm">{(schema?.required || []).length}</Text>
							</div>
						</div>

						{Object.keys(schema?.properties || {}).length > 0 && (
							<div>
								<Text type="secondary" className="text-[10px] uppercase tracking-wide block mb-1.5">Fields</Text>
								<div className="space-y-1">
									{Object.entries(schema.properties || {}).map(([key, prop]) => {
										const propSchema = prop as any;
										return (
											<div key={key} className="flex items-center gap-2 text-xs">
												<code className="bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">{key}</code>
												<Text type="secondary">{propSchema.type}{propSchema.enum ? ` (${propSchema.enum.length} options)` : ""}{propSchema.type === "array" ? ` of ${propSchema.items?.type || "items"}` : ""}</Text>
												{(schema.required || []).includes(key) && <Text type="danger" className="text-[10px]">required</Text>}
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>
				</TabPane>
			</Tabs>
		</Modal>
	);
};

export default SchemaPreviewModal;

